import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Navbar = ({ scrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { language, toggleLanguage, t } = useLanguage();
  
  // Scroll progress indicator
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => {
      setAtTop(window.scrollY < 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path, sectionId) => {
    setIsOpen(false);
    if (isHomePage && sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(path);
    }
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#C5A55C] origin-left z-[60]"
        style={{ scaleX }}
      />
      
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          atTop && !scrolled
            ? 'bg-gradient-to-b from-black/70 via-black/30 to-transparent py-8'
            : 'bg-black/95 backdrop-blur-sm py-4 border-b border-white/5'
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center relative">
          {/* Logo */}
          <motion.div
            whileHover={{ opacity: 0.8 }}
            className="cursor-pointer flex-shrink-0 relative z-10"
            onClick={handleLogoClick}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/573hpmih_Transparent-PNG.png"
              alt="Martinez Solutions"
              className={`transition-all duration-500 max-w-[180px] lg:max-w-[220px] object-contain ${atTop && !scrolled ? 'h-12' : 'h-8'}`}
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 relative z-20">
            <NavLink delay={0.1} onClick={() => handleNavigation('/', 'home')}>{t('nav.home')}</NavLink>
            <NavLink delay={0.15} onClick={() => handleNavigation('/servicios')}>{t('nav.services')}</NavLink>
            <NavLink delay={0.2} onClick={() => handleNavigation('/', 'projects')}>{t('nav.projects')}</NavLink>
            <NavLink delay={0.25} onClick={() => handleNavigation('/nosotros')}>{t('nav.about')}</NavLink>
            <NavLink delay={0.3} onClick={() => handleNavigation('/contacto')}>{t('nav.contact')}</NavLink>
            
            {/* Language Toggle Button */}
            <motion.button
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5, ease: 'easeOut' }}
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-[#C5A55C] text-white/70 hover:text-[#C5A55C] transition-all duration-300 font-montserrat font-medium text-xs tracking-wider"
              data-testid="language-toggle-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </motion.button>
            
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: 'easeOut' }}
            >
              <Link
                to="/contacto"
                className="ml-2 px-8 py-3 border border-[#C5A55C] text-[#C5A55C] bg-transparent hover:bg-[#C5A55C] hover:text-black transition-all duration-500 font-montserrat font-light text-xs tracking-[0.2em] uppercase inline-block"
                data-testid="nav-cta-btn"
              >
                {t('nav.consultation')}
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-4 lg:hidden">
            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 border border-white/20 text-white/70 font-montserrat font-medium text-xs"
              data-testid="mobile-language-toggle"
            >
              <Globe className="w-3 h-3" />
              {language === 'en' ? 'ES' : 'EN'}
            </button>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white hover:text-[#C5A55C] transition-colors"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-black border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-1">
              <MobileNavLink index={0} onClick={() => handleNavigation('/', 'home')}>{t('nav.home')}</MobileNavLink>
              <MobileNavLink index={1} onClick={() => handleNavigation('/servicios')}>{t('nav.services')}</MobileNavLink>
              <MobileNavLink index={2} onClick={() => handleNavigation('/', 'projects')}>{t('nav.projects')}</MobileNavLink>
              <MobileNavLink index={3} onClick={() => handleNavigation('/nosotros')}>{t('nav.about')}</MobileNavLink>
              <MobileNavLink index={4} onClick={() => handleNavigation('/contacto')}>{t('nav.contact')}</MobileNavLink>
              
              <motion.div 
                className="pt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <Link
                  to="/contacto"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-8 py-4 border border-[#C5A55C] text-[#C5A55C] bg-transparent hover:bg-[#C5A55C] hover:text-black transition-all duration-500 font-montserrat font-light text-sm tracking-[0.2em] uppercase"
                >
                  {t('nav.startProject')}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
    </>
  );
};

const NavLink = ({ children, onClick, delay = 0 }) => (
  <motion.button
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: 'easeOut' }}
    onClick={onClick}
    className="font-montserrat font-light text-xs tracking-[0.2em] uppercase text-white/70 hover:text-[#C5A55C] transition-colors duration-300 gold-underline pb-1 relative group"
    whileHover={{ y: -2 }}
  >
    {children}
    <motion.span 
      className="absolute -bottom-1 left-0 w-full h-[1px] bg-[#C5A55C] origin-left"
      initial={{ scaleX: 0 }}
      whileHover={{ scaleX: 1 }}
      transition={{ duration: 0.3 }}
    />
  </motion.button>
);

const MobileNavLink = ({ children, onClick, index = 0 }) => (
  <motion.button
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.1, duration: 0.3 }}
    onClick={onClick}
    className="block w-full text-left px-4 py-4 font-montserrat font-light text-sm tracking-[0.15em] uppercase text-white/70 hover:text-[#C5A55C] hover:bg-white/5 transition-all duration-300"
  >
    {children}
  </motion.button>
);

export default Navbar;
